import api from './api';
import type { Supplier, PaginatedResponse } from '../types/venue';
import type { SupplierItemLink } from '../types/supplierItemLink';

/**
 * venueSupplierService — read-only supplier access scoped to a single venue.
 *
 * Hits /admin/venue/:venueId/suppliers, which returns only suppliers the venue
 * has actually transacted with (via item-link, PO, or invoice). Used by:
 *   - VenueSupplierList / VenueSupplierDetail pages
 *   - Supplier dropdowns inside the venue workspace (PurchaseOrderForm,
 *     QuickReceiveModal, InventoryItemDetail "Add Supplier")
 *
 * Global supplier endpoints (`supplierService`) remain admin-only.
 */

const base = (venueId: string) => `/admin/venue/${venueId}/suppliers`;

const venueSupplierService = {
  listVenueSuppliers: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    q?: string,
    type?: string,
    sort = 'name',
    order: 'asc' | 'desc' = 'asc',
  ): Promise<PaginatedResponse<Supplier>> => {
    const params: Record<string, unknown> = { page, pageSize, sort, order };
    if (q?.trim()) params.q = q.trim();
    if (type) params.type = type;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  getVenueSupplier: async (venueId: string, supplierId: string): Promise<Supplier> => {
    const response = await api.get(`${base(venueId)}/${supplierId}`);
    return response.data;
  },

  /**
   * Item-links for one supplier within this venue. 404-equivalent (empty array)
   * if there are none. Never returns links from another venue.
   */
  getVenueSupplierItemLinks: async (
    venueId: string,
    supplierId: string,
  ): Promise<SupplierItemLink[]> => {
    const response = await api.get(`${base(venueId)}/${supplierId}/item-links`);
    return response.data;
  },
};

export default venueSupplierService;
